<template>
  <b-modal
      v-bind="$props"
      :id="id"
      centered
      footer-class="border-0 pt-0"
      hide-header
      no-close-on-backdrop
      no-close-on-esc
  >
    <validation-observer ref="form" tag="div">
      <div class="text-center mb-3">
        <h4>{{ approveConfirmTitle }}</h4>
        <p class="m-0" v-html="approveConfirmDescription"></p>
      </div>
      <validation-provider
          v-if="isLevelOneApproval"
          v-slot="{ validated, errors }"
          :name="lodash.startCase($t('sales_plans.modal_messages.approve_cashflow_label'))"
          rules="required|min_value:0"
          vid="cashflow"
      >
        <b-form-group
            :invalid-feedback="errors[0]"
            :label="`${lodash.startCase($t('sales_plans.modal_messages.approve_cashflow_label'))}`"
            label-class="sales-plan-modal-label"
            :state="errors[0] ? false : (validated ? true : null)"
            label-for="approve-cashflow"
            class="asterisk-after-text"
        >
          <numeral-strict-input
              id="approve-cashflow"
              v-model.number="form.cashflow"
              :placeholder="$t('sales_plans.modal_messages.approve_cashflow_placeholder')"
              :state="errors[0] ? false : (validated ? true : null)"
              aria-required="true"
              format="$ 0,0.[00]"
              name="cashflow"
              required
          />
        </b-form-group>
      </validation-provider>
      <validation-provider
          v-slot="{ validated, errors }"
          :name="lodash.startCase($t('sales_plans.modal_messages.approve_proof_label'))"
          rules="mimes:image/jpeg,image/png,application/pdf"
          vid="file"
      >
        <b-form-group
            :invalid-feedback="errors[0]"
            :label="lodash.startCase($t('sales_plans.modal_messages.approve_proof_label'))"
            label-class="sales-plan-modal-label"
            :state="errors[0] ? false : (validated ? true : null)"
            class="mt-3"
            label-for="approve-proof"
        >
          <b-form-file
              id="approve-proof"
              v-model="form.file"
              :drop-placeholder="$t('sales_plans.modal_messages.approve_proof_placeholder')"
              :placeholder="$t('sales_plans.modal_messages.approve_proof_placeholder')"
              :state="errors[0] ? false : (validated ? true : null)"
              accept="image/jpeg,image/png,application/pdf"
          />
        </b-form-group>
      </validation-provider>
      <b-form-group
          :label="lodash.startCase($t('sales_plans.modal_messages.approve_reason_label'))"
          label-class="sales-plan-modal-label"
          class="mt-3"
          label-for="approve-reason"
      >
        <b-form-textarea
            id="approve-reason"
            v-model="form.reason"
            :placeholder="$t('sales_plans.modal_messages.approve_reason_placeholder')"
            rows="4"
        />
      </b-form-group>
    </validation-observer>
    <template #modal-footer>
      <div class="d-flex justify-content-end gap-2 w-100">
        <b-button
            block
            variant="light"
            @click="handleCancel"
        >
          <b>{{ $t('Cancel') }}</b>
        </b-button>
        <b-button
            block
            class="m-0"
            variant="primary"
            @click="handleConfirm"
        >
          <b>{{ lodash.startCase($t('sales_plans.buttons.yes_confirm')) }}</b>
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import ConfirmationModal from '@/components/modals/confirmation-modal.vue'
import NumeralStrictInput from '@/components/inputs/numeral-strict';

export default {
  name: 'sales-plan-approve-confirmation',
  extends: ConfirmationModal,
  components: {
    NumeralStrictInput,
  },
  props: {
    id: {default: 'sales-plan-approve-confirmation-modal'},
    hideHeader: {default: true},
    title: {default: ''},
    salesPlan: {type: Object, default: () => ({})},
  },
  data() {
    return {
      options: {
        message: null,
        approvalLevel: null,
      },
      form: {
        cashflow: null,
        file: null,
        reason: '',
      },
      resolvePromise: null,
    };
  },
  computed: {
    approvalLevel() {
      const level = this.options.approvalLevel
          ?? this.salesPlan?.current_approval_level
          ?? this.salesPlan?.approval_progress?.current_level
          ?? this.salesPlan?.approval_progress?.current_step?.level

      const numericLevel = Number(level)
      return Number.isFinite(numericLevel) ? numericLevel : null
    },
    isLevelOneApproval() {
      return this.approvalLevel === 1
    },
    approveConfirmTitle() {
      return this.options.title || `${this.$t('sales_plans.modal_messages.confirm_approve_title')}?`
    },
    approveConfirmDescription() {
      if (this.options.message) {
        return this.options.message
      }

      const description = this.$t('sales_plans.modal_messages.{something}_confirm_approve_description')
      const safeSomething = this.lodash.escape(this.salesPlan?.name || '-')

      return description.replace('{something}', `<strong>${safeSomething}</strong>`)
    }
  },
  methods: {
    show(options = {}) {
      return new Promise((resolve) => {
        this.options = {
          message: null,
          approvalLevel: null,
          ...options,
        }
        this.form.cashflow = null
        this.form.file = null
        this.form.reason = ''
        this.$bvModal.show(this.id)

        this.resolvePromise = resolve

        this.$nextTick(() => {
          this.$refs.form?.reset()
        })
      });
    },
    async handleConfirm() {
      if (!this.$refs.form) {
        return
      }

      const isValid = await this.$refs.form.validate()
      if (!isValid) {
        return
      }

      if (this.resolvePromise) {
        const result = {
          file: this.form.file,
          reason: this.form.reason.trim(),
        }

        if (this.isLevelOneApproval) {
          result.cashflow = this.form.cashflow
        }

        this.resolvePromise(result)
      }
      this.hide()
    },
    handleCancel() {
      if (this.resolvePromise) {
        this.resolvePromise(false)
      }
      this.hide()
    },
    hide() {
      this.$bvModal.hide(this.id)
    },
  }
}
</script>

<style scoped lang="scss">
@import "../../design/custom/components/sales-plan-label";

::v-deep {
  .sales-plan-modal-label {
    @include sales-plan-label;
  }

  .asterisk-after-text {
    label::after {
      content: " *";
      color: #DC2626;
    }
  }

  .modal-body {
    color: var(--neutral-gray-800, #454646);
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
  }

  .modal-footer {
    border: none;
  }
}
</style>
